// ═══════════════════════════════════════════════════════════════════
// ieBackdropCore.js — THE BACKDROP BAND'S BRAIN (plain JS: no React, no DOM UI)
// MASTER COPY: gamemaster/engine/ieBackdropCore.js — vendored byte-identical.
// React titles use it through ieBackdrop.js; a vanilla title (Sweet Dreams)
// imports it directly. One store, so the band behaves the same everywhere.
// ───────────────────────────────────────────────────────────────────
// caP'n, 2026-10-21: "alllll of that non essential screen clutter should be
// housed in there in every game, world chat all of it … world chat should
// appear in the backdrop like the log" — "combine it now with Terror's
// backdrop … I don't want it broken … also combine it with Sweet Dreams',
// we need continuity".
//
// ⭐ THIS IS TERROR'S RAIL, GENERALISED (app-terror/src/railDock.js, 2026-09-23:
// "Clicking on one thing at a time should minimize another" + "I want it able
// to be minimized into just 'Menu'" + "you can also move it around").
// The store keeps railDock's exact shape and semantics, so Terror can sit on
// it unchanged:
//   panel      the ONE band panel open ("menu" | "log" | "friends" | …) or null.
//              Opening one closes whichever was open; tapping it again closes it.
//   collapsed  the whole band folded into one draggable ☰ MENU pill.
//   pill       {x,y} where the folded pill was dragged to (null = home slot).
//   hosts      how many screens host a band right now (0 = no band anywhere).
//   hostTop / consoleTop   the first y a panel may start at (their max wins).
//   hostBottom the floor a panel stops at (Aemilia's nook lives under it).
// Added for the Empire: rows + chips + tabs (who is IN the band), and hidden
// (login, indoors). `collapsed` + `pill` persist per title (configure()).
//
// WHERE THE BAND IS: desktop / landscape only (≤768px or portrait = no band;
// the title keeps a MENU pill that opens the same contents as a sheet).
// ═══════════════════════════════════════════════════════════════════

// ── Geometry — the ONE place the band is measured (THE ONE-HELPER LAW) ──
export const BAND = {
  RIGHT: 12,                  // px from the right edge
  W: "min(280px, 24vw)",      // the band's content width (Terror's RAIL_W)
  MENU_TOP: 12,               // the ☰ MENU pill
  ROW_TOP: 60,                // the tab row (fold · log · friends · …)
  ROW_H: 44,
  SLOT_TOP: 112,              // first y a panel may start on
  SLOT_BOTTOM: 70,            // default floor
  TAB_H: 40,                  // Terror's Record header stays as a tab above a panel
  BREAK: 768,
};
// The width the band takes out of the screen, in px — what a 3D canvas or a
// viewport reserves. clientWidth, never innerWidth (the scrollbar trap).
export function bandWidthPx() {
  if (typeof document === "undefined") return 0;
  const vw = document.documentElement.clientWidth || window.innerWidth;
  return Math.round(Math.min(280, vw * 0.24) + BAND.RIGHT * 2);
}
export function isDesktop() {
  if (typeof window === "undefined") return false;
  const narrow = window.matchMedia && window.matchMedia(`(max-width: ${BAND.BREAK}px)`).matches;
  return !narrow && !(window.innerHeight > window.innerWidth);
}

// ── Persistence (per title) ─────────────────────────────────────────
let LS_KEY = "ie.band.v1";
let DEFAULT_PANEL = null;
function load() {
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const v = JSON.parse(raw);
    return {
      collapsed: !!v.collapsed,
      pill: v.pill && Number.isFinite(v.pill.x) && Number.isFinite(v.pill.y) ? v.pill : null,
    };
  } catch { return {}; }
}
function persist() {
  try { window.localStorage.setItem(LS_KEY, JSON.stringify({ collapsed: S.collapsed, pill: S.pill })); }
  catch { /* private window / blocked storage — the band still works */ }
}

// ── The store ───────────────────────────────────────────────────────
let S = {
  panel: null, collapsed: false, pill: null,
  hosts: 0, hostTop: BAND.SLOT_TOP, consoleTop: 0, hostBottom: BAND.SLOT_BOTTOM,
  faces: 0,                     // mounted SHARED faces (<MenuBackdrop/>). Terror hosts the band
                                // with its own face (railDock's pills), so faces stays 0 there
  hidden: false,
  quiet: false,                 // a title modal is up: the band shows its painting only
  items: {}, chips: {}, tabs: {},
  desktop: isDesktop(),
};
const hideReasons = new Set();
const listeners = new Set();
function emit(next) { S = { ...S, ...next }; listeners.forEach((l) => { try { l(); } catch { /* one bad listener never stops the rest */ } }); }

if (typeof window !== "undefined") {
  const onResize = () => { const d = isDesktop(); if (d !== S.desktop) emit({ desktop: d }); };
  window.addEventListener("resize", onResize);
  window.addEventListener("orientationchange", onResize);
}

export const band = {
  get: () => S,
  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },

  /** A title names its storage key and its resting panel ONCE, before first paint. */
  configure({ storageKey, defaultPanel } = {}) {
    if (storageKey) LS_KEY = storageKey;
    if (defaultPanel !== undefined) DEFAULT_PANEL = defaultPanel;
    emit({ ...load(), panel: S.panel == null ? DEFAULT_PANEL : S.panel });
  },

  // railDock's verbs, byte-for-byte in behaviour — except "menu", which on a
  // computer closes back to the resting panel (the log never just vanishes)
  toggle(id) { emit({ panel: S.panel === id ? restAfter(id) : id, collapsed: false }); persist(); },
  open(id) { emit({ panel: id, collapsed: false }); persist(); },
  close(id) { if (!id || S.panel === id) emit({ panel: restAfter(id) }); },
  setQuiet(v) { if (!!v !== S.quiet) emit({ quiet: !!v }); },
  collapse() { emit({ collapsed: true }); persist(); },
  expand() { emit({ collapsed: false }); persist(); },
  setPill(p) { emit({ pill: p }); persist(); },
  setConsoleTop(t) { if (t !== S.consoleTop) emit({ consoleTop: t }); },

  /** A screen that hosts the band. Returns the release. */
  addHost(top = BAND.SLOT_TOP, bottom = BAND.SLOT_BOTTOM) {
    emit({ hosts: S.hosts + 1, hostTop: top, hostBottom: bottom });
    let done = false;
    return () => { if (done) return; done = true; emit({ hosts: Math.max(0, S.hosts - 1) }); };
  },

  /** The shared face (<MenuBackdrop/>) counts itself in. Returns the release. */
  addFace() {
    emit({ faces: S.faces + 1 });
    let done = false;
    return () => { if (done) return; done = true; emit({ faces: Math.max(0, S.faces - 1) }); };
  },

  /** Hide the whole band (login, indoors, a cutscene). Reasons stack. */
  setHidden(v, reason = "title") {
    if (v) hideReasons.add(reason); else hideReasons.delete(reason);
    const h = hideReasons.size > 0;
    if (h !== S.hidden) emit({ hidden: h, ...(h && S.panel === "menu" && !S.desktop ? { panel: null } : {}) });
  },

  /** Rows (the menu), chips (the header), tabs (band panels) — keyed by owner. */
  publish(kind, key, list) { emit({ [kind]: { ...S[kind], [key]: list } }); },
  unpublish(kind, key) {
    const gone = S[kind][key]; if (!gone) return;
    const n = { ...S[kind] }; delete n[key];
    // a tab that disappears (the party ended) never leaves its panel open on nothing
    const orphan = kind === "tabs" && gone.some((t) => t.id === S.panel);
    emit({ [kind]: n, ...(orphan ? { panel: DEFAULT_PANEL === S.panel ? null : DEFAULT_PANEL } : {}) });
  },
};

function restAfter(id) { return id === "menu" && S.desktop && DEFAULT_PANEL !== "menu" ? DEFAULT_PANEL : null; }

let _owner = 0;
export const newOwnerKey = () => "o" + (++_owner);

/** Derived facts every face reads. */
export function bandView(s = S) {
  const live = s.hosts > 0 && !s.hidden;
  return {
    ...s,
    live,                                   // a band host is mounted and not hidden
    active: live && s.desktop,              // the BAND is on screen (desktop/landscape) — railDock's `active`
    faced: s.faces > 0,                     // the SHARED face draws it (tabs/rows/sheet); false in Terror,
                                            // whose own pills draw its band — shared chrome then stays out
    sheet: live && !s.desktop && s.faces > 0, // phone/portrait with the shared face: pill + sheet
    slotTop: Math.max(s.hostTop, s.consoleTop),
    rows: Object.values(s.items).flat(),
    chipList: Object.values(s.chips).flat(),
    tabList: Object.values(s.tabs).flat().sort((a, b) => (a.order || 0) - (b.order || 0)),
  };
}

/** The fixed box every band panel fills (railPanelStyle, as plain numbers/strings). */
export function panelBox(slotTop = Math.max(S.hostTop, S.consoleTop), slotBottom = S.hostBottom, tab = BAND.TAB_H) {
  return {
    position: "fixed",
    right: BAND.RIGHT, width: BAND.W,
    top: slotTop + tab, bottom: slotBottom,
    left: "auto", maxHeight: "none",
  };
}
